import { useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";

const MIN = 240;
const DEFAULT = 360;

/**
 * Draggable divider between the side panel and the main column of a
 * `.split-layout`. The width lives in a CSS variable on the layout element
 * (`--split-side`) and is remembered per page in localStorage; double-click
 * resets it. Below 820px the layout is one column and the handle is hidden.
 * `side: "right"` for a panel on the right of the handle (layout screen).
 */
export function useSplitWidth(storageKey: string, fallback = DEFAULT, opts: { side?: "left" | "right" } = {}) {
  // A right-hand panel grows when the handle is dragged LEFT.
  const sign = opts.side === "right" ? -1 : 1;
  const [width, setWidth] = useState<number>(() => {
    try {
      const v = Number(localStorage.getItem(storageKey));
      return Number.isFinite(v) && v >= MIN ? v : fallback;
    } catch {
      return fallback;
    }
  });
  const widthRef = useRef(width);
  useEffect(() => { widthRef.current = width; }, [width]);

  const clamp = useCallback((w: number) => Math.min(Math.max(MIN, w), Math.max(MIN, window.innerWidth * 0.6)), []);

  const commit = useCallback((w: number) => {
    const c = clamp(w);
    setWidth(c);
    try { localStorage.setItem(storageKey, String(Math.round(c))); } catch { /* ignore */ }
  }, [clamp, storageKey]);

  const reset = useCallback(() => commit(fallback), [commit, fallback]);

  const onPointerDown = useCallback((e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const startX = e.clientX;
    const startW = widthRef.current;
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const move = (ev: globalThis.PointerEvent) => setWidth(clamp(startW + sign * (ev.clientX - startX)));
    const up = (ev: globalThis.PointerEvent) => {
      target.removeEventListener("pointermove", move);
      target.removeEventListener("pointerup", up);
      target.removeEventListener("pointercancel", up);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      commit(startW + sign * (ev.clientX - startX));
    };
    target.addEventListener("pointermove", move);
    target.addEventListener("pointerup", up);
    target.addEventListener("pointercancel", up);
  }, [clamp, commit, sign]);

  const layoutStyle = { "--split-side": `${width}px` } as CSSProperties;
  return { width, layoutStyle, handleProps: { onPointerDown, onDoubleClick: reset } };
}

export function SplitHandle({ onPointerDown, onDoubleClick }: {
  onPointerDown: (e: PointerEvent<HTMLDivElement>) => void;
  onDoubleClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="split-handle"
      role="separator"
      aria-orientation="vertical"
      title={t("split.handleHint", "Prevuci za širinu panela; dupli klik vraća podrazumevanu")}
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
    >
      <span />
    </div>
  );
}
